import type { NextRequest } from 'next/server';

/** Subdomain label for staff intranet (intranet.localhost, intranet.example.com). */
export const INTRANET_SUBDOMAIN =
  process.env.INTRANET_SUBDOMAIN ?? 'intranet';

/** Path prefix on the public domain until a custom subdomain is configured. */
export const INTRANET_PATH_PREFIX = '/intranet';

export const INTRANET_CONTEXT_HEADER = 'x-intranet-context';

/** Auth pages that stay on the dedicated `/intranet/auth/*` app routes (Microsoft login). */
export const INTRANET_PUBLIC_AUTH_SUFFIXES = [
  '/auth/login',
  '/auth/signup',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/confirm-email',
] as const;

export function stripPort(host: string): string {
  return host.split(':')[0]?.toLowerCase() ?? '';
}

export function isIntranetHost(host: string): boolean {
  const hostname = stripPort(host);
  return (
    hostname === `${INTRANET_SUBDOMAIN}.localhost` ||
    hostname.startsWith(`${INTRANET_SUBDOMAIN}.`)
  );
}

export function isIntranetPath(pathname: string): boolean {
  return (
    pathname === INTRANET_PATH_PREFIX ||
    pathname.startsWith(`${INTRANET_PATH_PREFIX}/`)
  );
}

/** True when the request should use intra-home CMS content (subdomain or /intranet path). */
export function isIntranetContext(request: NextRequest): boolean {
  const host = request.headers.get('host') ?? '';
  return isIntranetHost(host) || isIntranetPath(request.nextUrl.pathname);
}

export function isIntranetApiRequest(request: NextRequest): boolean {
  if (isIntranetContext(request)) return true;
  if (request.headers.get(INTRANET_CONTEXT_HEADER) === 'true') return true;
  const referer = request.headers.get('referer');
  if (!referer) return false;
  try {
    // Parse instead of substring-matching so the bare `/intranet` home counts too.
    const { host, pathname } = new URL(referer);
    return isIntranetHost(host) || isIntranetPath(pathname);
  } catch {
    return false;
  }
}

export function getPublicSiteUrl(): string {
  return (
    process.env.NEXT_PUBLIC_FRONTEND_URL?.replace(/\/$/, '') ||
    'http://localhost:3000'
  );
}

export function getIntranetBaseUrl(): string {
  const configured = process.env.NEXT_PUBLIC_INTRANET_URL?.replace(/\/$/, '');
  if (configured) return configured;

  const siteUrl = getPublicSiteUrl();
  if (siteUrl.includes('localhost')) {
    return `http://${INTRANET_SUBDOMAIN}.localhost:3000`;
  }

  return `${siteUrl}${INTRANET_PATH_PREFIX}`;
}

/** Login path as seen in the browser (path mode vs subdomain). */
export function getIntranetLoginPath(host?: string): string {
  if (host && isIntranetHost(host)) {
    return '/auth/login';
  }
  return `${INTRANET_PATH_PREFIX}/auth/login`;
}

/** Dedicated intranet app paths that must NOT rewrite into shared (home) CMS routes. */
export function isIntranetAppPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/$/, '') || '/';
  if (
    normalized === `${INTRANET_PATH_PREFIX}/area-personale` ||
    normalized.startsWith(`${INTRANET_PATH_PREFIX}/area-personale/`)
  ) {
    return true;
  }

  return INTRANET_PUBLIC_AUTH_SUFFIXES.some(
    (suffix) =>
      normalized === `${INTRANET_PATH_PREFIX}${suffix}` ||
      normalized.startsWith(`${INTRANET_PATH_PREFIX}${suffix}/`),
  );
}

/** On intranet host, map browser auth/profile URLs onto `/intranet/*` app routes. */
export function toIntranetAppRewritePath(pathname: string): string | null {
  const normalized = pathname.replace(/\/$/, '') || '/';

  if (normalized === '/area-personale' || normalized.startsWith('/area-personale/')) {
    return `${INTRANET_PATH_PREFIX}${normalized}`;
  }

  for (const suffix of INTRANET_PUBLIC_AUTH_SUFFIXES) {
    if (normalized === suffix || normalized.startsWith(`${suffix}/`)) {
      return `${INTRANET_PATH_PREFIX}${normalized}`;
    }
  }

  return null;
}

export function toIntranetRewritePath(pathname: string): string {
  if (pathname === '/') return INTRANET_PATH_PREFIX;
  if (pathname.startsWith(INTRANET_PATH_PREFIX)) return pathname;
  return `${INTRANET_PATH_PREFIX}${pathname}`;
}

export function getIntranetEffectivePath(pathname: string): string {
  if (isIntranetPath(pathname)) return pathname;
  if (pathname === '/') return INTRANET_PATH_PREFIX;
  return `${INTRANET_PATH_PREFIX}${pathname}`;
}

export function isIntranetPublicAuthPath(effectivePath: string): boolean {
  return INTRANET_PUBLIC_AUTH_SUFFIXES.some(
    (suffix) =>
      effectivePath === `${INTRANET_PATH_PREFIX}${suffix}` ||
      effectivePath.endsWith(suffix),
  );
}

export function getIntranetLinkPrefix(pathname: string): string {
  return pathname.startsWith(INTRANET_PATH_PREFIX) ? INTRANET_PATH_PREFIX : '';
}

/** Strip /intranet prefix for internal routing on the public host (CMS pages). */
export function toPublicPathFromIntranetPath(pathname: string): string {
  if (pathname === INTRANET_PATH_PREFIX) return '/';
  if (pathname.startsWith(`${INTRANET_PATH_PREFIX}/`)) {
    return pathname.slice(INTRANET_PATH_PREFIX.length) || '/';
  }
  return pathname;
}
